import React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTheme } from "styled-components";
import Box from "@mui/material/Box";
import useMediaQuery from "../../../../hooks/useMediaQuerryHook";
import {
  GalleryData,
  GenProjectFormComponetProps,
  Links,
  ProjectDataProps,
  SelectOption,
} from "../../../common/types";
import { SingleValue } from "react-select";
import {
  handleProjectFormBlur,
  ProjectvalidateField,
} from "../../../../utils/Validations/adminFormValidationUtil";
import { Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import useDatePickerSlotProps from "../../../../utils/CustomizedLibraries/datePickerSlotsProps";



const MuiDatePicker: React.FC<GenProjectFormComponetProps> = ({
  projectData,
  errors,
  touched,
  setProjectData,
  setErrors,
  setTouched,
}) => {
  // const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const defaultStyledTheme = useTheme();
  const isMobile = useMediaQuery(576);
  const sharedSlotsProps = useDatePickerSlotProps();

  const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
    color: defaultStyledTheme.text, // Default square border color
    "&.Mui-checked": {
      color: defaultStyledTheme.circularIconBg, // Square and tick color when checked
    },
    "&.MuiCheckbox-root:hover": {
      backgroundColor: defaultStyledTheme.checkBoxLightHover, // Light hover background
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.7rem", // Adjust size if needed
    },
  }));



  //validations
  const handleBlurWrapper = (
    name: keyof ProjectDataProps,
    value:
      | string
      | number
      | SingleValue<SelectOption>
      | GalleryData
      | Partial<Links>
  ) => {
    handleProjectFormBlur(
      name,
      value,
      touched,
      setTouched,
      validateFieldWrapper
    );
  };

  const validateFieldWrapper = (
    name: keyof ProjectDataProps,
    value:
      | string
      | SingleValue<SelectOption>
      | number
      | GalleryData
      | Partial<Links>
  ) => {
    ProjectvalidateField(name, value, errors, setErrors, projectData);
  };

  // Handle date changes
  const handleDateChange = (date: Date | null, name: string) => {
    const timestamp = date ? date.getTime() : 0;

    if (name === "dateStarted" || name === "endDate") {
      setProjectData((prev) => ({
        ...prev,
        [name]: timestamp,
      }));
    }

    // console.log(`out ${name}`, value);
    validateFieldWrapper(name as keyof ProjectDataProps, timestamp);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setProjectData((prev) => ({
      ...prev,
      isOngoing: isChecked,
      endDate: isChecked ? 0 : prev.endDate, // If ongoing, set endDate to 0
    }));

    // Clear errors for endDate if ongoing is checked
    if (isChecked) {
      ProjectvalidateField(
        "endDate",
        null,
        errors,
        setErrors,
        { ...projectData, isOngoing: isChecked } // Pass updated projectData
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControlLabel
        control={
          <CustomCheckbox
          id="isOngoingCheckbox"
          name="isOngoing"
            checked={projectData.isOngoing}
            onChange={handleCheckboxChange}
          />
        }
        label="Ongoing Project"
      />

      <Box
        sx={{
          marginTop: "10px",
          display: "flex",
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: 2,
        }}
      >
        <DatePicker
          label="Date started"
          name="dateStarted"
          value={
            projectData.dateStarted ? new Date(projectData.dateStarted) : null
          }
          onChange={(date) => handleDateChange(date, "dateStarted")}
          slotProps={{
            ...sharedSlotsProps,
            textField: {
              ...sharedSlotsProps.textField,
              helperText: errors.dateStarted,
              error: !!errors.dateStarted,
              onBlur: () =>
                handleBlurWrapper("dateStarted", projectData.dateStarted),
            },
          }}
        />
        <DatePicker
          label="Date ended"
          name="endDate"
          value={projectData.endDate ? new Date(projectData.endDate) : null}
          onChange={(date) => handleDateChange(date, "endDate")}
          disabled={projectData.isOngoing} // Disable when "Ongoing" is checked
          slotProps={{
            ...sharedSlotsProps,
            textField: {
              ...sharedSlotsProps.textField,
              helperText: errors.endDate,
              error: !!errors.endDate,
              onBlur: () => handleBlurWrapper("endDate", projectData.endDate),
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default MuiDatePicker;
